import { TanStackPostalCodeField } from "@sellar-io/components/adapters";
import { Button, InputNote } from "@sellar-io/components";
import { useForm } from "@tanstack/react-form";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { CountryCodeDictionary } from "@sellar-io/common";
import { CheckoutAddress } from "./types";

type CheckoutEditAddressFormProps = {
  initialData: CheckoutAddress;
  onConfirm: (address: CheckoutAddress) => void;
};

export const CheckoutEditAddressForm = ({
  initialData,
  onConfirm,
}: CheckoutEditAddressFormProps) => {
  const form = useForm({
    defaultValues: initialData,
    onSubmit: ({ value }) => onConfirm(value),
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.Field
        name="name"
        validators={{
          onSubmit: ({ value }) =>
            !value ? "Address line 1 is required" : undefined,
        }}
      >
        {(field) => (
          <>
            <InputContainer>
              <InputText
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                placeholder="Address line 1"
              />
            </InputContainer>
            {field.state.meta.errors.length > 0 && (
              <InputNote type="error">
                {field.state.meta.errors.join(", ")}
              </InputNote>
            )}
          </>
        )}
      </form.Field>
      <form.Field name="street">
        {(field) => (
          <>
            <InputContainer>
              <InputText
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                placeholder="Address line 2 (optional)"
              />
            </InputContainer>
            {field.state.meta.errors.length > 0 && (
              <InputNote type="error">
                {field.state.meta.errors.join(", ")}
              </InputNote>
            )}
          </>
        )}
      </form.Field>
      <form.Field
        name="city"
        validators={{
          onSubmit: ({ value }) => (!value ? "City is required" : undefined),
        }}
      >
        {(field) => (
          <>
            <InputContainer>
              <InputText
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                placeholder="City"
              />
            </InputContainer>
            {field.state.meta.errors.length > 0 && (
              <InputNote type="error">
                {field.state.meta.errors.join(", ")}
              </InputNote>
            )}
          </>
        )}
      </form.Field>
      <form.Field name="region">
        {(field) => (
          <>
            <InputContainer>
              <InputText
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                placeholder="Region"
              />
            </InputContainer>
            {field.state.meta.errors.length > 0 && (
              <InputNote type="error">
                {field.state.meta.errors.join(", ")}
              </InputNote>
            )}
          </>
        )}
      </form.Field>
      <TanStackPostalCodeField
        form={form}
        name="postalCode"
        countryField="country"
      />
      <form.Field
        name="country"
        validators={{
          onSubmit: ({ value }) => (!value ? "Country is required" : undefined),
        }}
      >
        {(field) => (
          <InputContainer>
            <InputSelect
              name={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) =>
                field.handleChange(
                  event.target.value as CheckoutAddress["country"],
                )
              }
              required
              placeholder="Country"
            >
              {Object.entries(CountryCodeDictionary).map(([key, label]) => (
                <InputOption key={key} value={key}>
                  {label}
                </InputOption>
              ))}
            </InputSelect>
          </InputContainer>
        )}
      </form.Field>
      <Button type="submit" className="w-full">
        Update
      </Button>
    </form>
  );
};
